import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useLocalStorage } from "../../../../utils/localStorage";
import { useAppInsights } from "../../../../services/appInsights";
import { TelemetryEvents } from "../../../../telemetry/eventNames";
import { TelemetryProp, toTelemetryProps } from "../../../../telemetry/telemetryProps";
import { TelemetrySurface } from "../../../../telemetry/surfaces";

export type DueDateFilterValue = "all" | "overdue" | "7" | "30";
export type DisputedFilterValue = "all" | "disputed" | "not_disputed";

export type SuggestedChasableFiltersState = {
  disputedFilter: DisputedFilterValue;
  disputedReasonFilter: string[];
  dueDateFilter: DueDateFilterValue;
  currencyFilter: string[];
};

const defaultFilters: SuggestedChasableFiltersState = {
  disputedFilter: "all",
  disputedReasonFilter: [],
  dueDateFilter: "all",
  currencyFilter: [],
};

type ContextValue = SuggestedChasableFiltersState & {
  onDisputedFilterChange(disputedFilter: DisputedFilterValue): void;
  onDisputedReasonFilterChange(disputedReasonFilter: string[]): void;
  onDueDateFilterChange(dueDateFilter: DueDateFilterValue): void;
  onCurrencyFilterChange(currencyFilter: string[]): void;
  clearAllFilters(): void;
};

const SuggestedChasableFiltersContext = createContext<ContextValue | null>(null);

function mergeStoredFilters(
  stored: Partial<SuggestedChasableFiltersState> | null | undefined
): SuggestedChasableFiltersState {
  if (!stored) return defaultFilters;
  return {
    disputedFilter:
      stored.disputedFilter && ["all", "disputed", "not_disputed"].includes(stored.disputedFilter)
        ? stored.disputedFilter
        : defaultFilters.disputedFilter,
    disputedReasonFilter: Array.isArray(stored.disputedReasonFilter)
      ? stored.disputedReasonFilter
      : defaultFilters.disputedReasonFilter,
    dueDateFilter:
      stored.dueDateFilter && ["all", "overdue", "7", "30"].includes(stored.dueDateFilter)
        ? stored.dueDateFilter
        : defaultFilters.dueDateFilter,
    currencyFilter: Array.isArray(stored.currencyFilter) ? stored.currencyFilter : defaultFilters.currencyFilter,
  };
}

export function SuggestedChasableFiltersProvider({ children }: { children: React.ReactNode }) {
  const localStorage = useLocalStorage();
  const { trackEvent } = useAppInsights();
  const [state, setState] = React.useState<SuggestedChasableFiltersState>(() => {
    const stored = localStorage?.getItem<SuggestedChasableFiltersState>(localStorage.keys.suggestedChasableFilters);
    return mergeStoredFilters(stored);
  });

  const persist = useCallback(
    (next: SuggestedChasableFiltersState) => {
      localStorage?.setItem(localStorage.keys.suggestedChasableFilters, next);
      return next;
    },
    [localStorage]
  );

  const onDisputedFilterChange = useCallback(
    (disputedFilter: DisputedFilterValue) => {
      trackEvent({
        name: TelemetryEvents.receivableFilterUsed,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
          [TelemetryProp.filterType]: "disputed",
          [TelemetryProp.value]: disputedFilter,
        }),
      });
      setState((prev) => persist({ ...prev, disputedFilter }));
    },
    [persist, trackEvent]
  );

  const onDisputedReasonFilterChange = useCallback(
    (disputedReasonFilter: string[]) => {
      trackEvent({
        name: TelemetryEvents.receivableFilterUsed,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
          [TelemetryProp.filterType]: "disputed_reason",
          [TelemetryProp.value]: disputedReasonFilter.join(",") || "none",
        }),
      });
      setState((prev) => persist({ ...prev, disputedReasonFilter }));
    },
    [persist, trackEvent]
  );

  const onDueDateFilterChange = useCallback(
    (dueDateFilter: DueDateFilterValue) => {
      trackEvent({
        name: TelemetryEvents.receivableFilterUsed,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
          [TelemetryProp.filterType]: "due_date",
          [TelemetryProp.value]: dueDateFilter,
        }),
      });
      setState((prev) => persist({ ...prev, dueDateFilter }));
    },
    [persist, trackEvent]
  );

  const onCurrencyFilterChange = useCallback(
    (currencyFilter: string[]) => {
      trackEvent({
        name: TelemetryEvents.receivableFilterUsed,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
          [TelemetryProp.filterType]: "currency",
          [TelemetryProp.value]: currencyFilter.join(",") || "none",
        }),
      });
      setState((prev) => persist({ ...prev, currencyFilter }));
    },
    [persist, trackEvent]
  );

  const clearAllFilters = useCallback(() => {
    trackEvent({
      name: TelemetryEvents.receivableFilterUsed,
      properties: toTelemetryProps({
        [TelemetryProp.surface]: TelemetrySurface.receivablesTable,
        [TelemetryProp.filterType]: "clear",
      }),
    });
    setState((prev) => persist({ ...prev, ...defaultFilters }));
  }, [persist, trackEvent]);

  const value = useMemo<ContextValue>(
    () => ({
      ...state,
      onDisputedFilterChange,
      onDisputedReasonFilterChange,
      onDueDateFilterChange,
      onCurrencyFilterChange,
      clearAllFilters,
    }),
    [
      state,
      onDisputedFilterChange,
      onDisputedReasonFilterChange,
      onDueDateFilterChange,
      onCurrencyFilterChange,
      clearAllFilters,
    ]
  );

  return <SuggestedChasableFiltersContext.Provider value={value}>{children}</SuggestedChasableFiltersContext.Provider>;
}

export function useSuggestedChasableFilters() {
  const ctx = useContext(SuggestedChasableFiltersContext);
  if (!ctx) {
    throw new Error("useSuggestedChasableFilters must be used within SuggestedChasableFiltersProvider");
  }
  return ctx;
}
